import React from 'react';
import { Copy, Edit3, Trash2, ShieldX, MapPin, Star, Home, Users, Layers3, BedDouble } from 'lucide-react';

import './AccommodationCard.css';

const AccommodationCard = ({ accommodation, onView, onEdit, onDelete, onReject, onDuplicate }) => {
    if (!accommodation) return null;

    const {
        title,
        address,
        propertyType,
        propertyCategory,
        units = [],
        maxGuests,
        rating = {},
        status,
        organizerName,
        media = [],
        cardImages = []
    } = accommodation;

    // ---------------------------------------------
    // IMAGE
    // ---------------------------------------------

    const image = cardImages?.[0] || media?.find((item) => item?.type === 'image')?.url || media?.[0]?.url || '';

    // ---------------------------------------------
    // LOCATION
    // ---------------------------------------------

    const location = [address?.city, address?.state].filter(Boolean).join(', ');

    // ---------------------------------------------
    // PRICE
    // ---------------------------------------------

    const prices = units
        .flatMap((unit) => unit?.pricing || [])
        .map((pricing) => {
            const price = pricing?.actualPrice ?? pricing?.basePrice ?? 0;

            return Number(price);
        })
        .filter((price) => price > 0);

    const startingPrice = prices.length > 0 ? Math.min(...prices) : null;

    const priceUnit = units?.[0]?.pricing?.[0]?.priceUnit || 'per_night';

    const priceUnitLabel =
        priceUnit === 'per_person'
            ? 'person'
            : priceUnit === 'per_unit'
              ? 'unit'
              : priceUnit === 'per_bed'
                ? 'bed'
                : 'night';

    // ---------------------------------------------
    // INVENTORY
    // ---------------------------------------------

    const totalInventory = units.reduce((total, unit) => total + Number(unit?.inventory?.totalUnits || 0), 0);

    // ---------------------------------------------
    // RATING
    // ---------------------------------------------

    const ratingValue = Number(rating?.average || rating?.value || 0);

    const reviewCount = Number(rating?.count || rating?.reviews || 0);

    // ---------------------------------------------
    // STATUS
    // ---------------------------------------------

    const getStatusClass = () => {
        switch (status) {
            case 'active':
                return 'status-active';

            case 'inactive':
                return 'status-inactive';

            case 'rejected':
                return 'status-rejected';

            default:
                return 'status-default';
        }
    };

    const getStatusLabel = () => {
        switch (status) {
            case 'active':
                return 'Approved';

            case 'inactive':
                return 'Pending';

            case 'rejected':
                return 'Rejected';

            default:
                return status || 'Unknown';
        }
    };

    // ---------------------------------------------
    // PROPERTY TYPE
    // ---------------------------------------------

    const formattedPropertyType = propertyType
        ? propertyType.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
        : 'Accommodation';

    const formattedCategory = propertyCategory
        ? propertyCategory.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
        : null;

    /*
     * The title's onClick used to call onView?.(accommodation) - the
     * whole object, not an id. Every caller (SupOrgPackList's
     * handleSelect, for one) treats its argument as an id string and
     * interpolates it straight into a URL, so a click here built a
     * URL like `/support/c/details/<orgId>/[object Object]` and
     * silently opened a broken tab - which is why nothing useful
     * seemed to happen. Only the title text was clickable too, not
     * the rest of the card (which already had a hover shadow
     * suggesting it should be). Fixed by moving the click handler to
     * the whole card and passing accommodation._id, with the action
     * buttons stopping propagation so Edit/Duplicate/Delete/Reject
     * don't also trigger the view navigation.
     */
    const handleCardClick = () => onView?.(accommodation._id);

    const stopAndRun = (handler) => (event) => {
        event.stopPropagation();
        handler();
    };

    return (
        <div className='accommodation-card' onClick={handleCardClick}>
            {/* =========================================
                IMAGE
            ========================================= */}

            <div className='accommodation-card-image-wrapper'>
                {image ? (
                    <img src={image} alt={title || 'Accommodation'} className='accommodation-card-image' />
                ) : (
                    <div className='accommodation-card-image-placeholder'>
                        <Home size={34} />
                    </div>
                )}

                {/* Status */}
                <div className={`accommodation-status ${getStatusClass()}`}>{getStatusLabel()}</div>
            </div>

            {/* =========================================
                MAIN CONTENT
            ========================================= */}

            <div className='accommodation-card-content'>
                <div className='accommodation-card-top'>
                    <div className='accommodation-card-info'>
                        <h3 className='accommodation-card-title'>{title || 'Untitled Camp'}</h3>

                        {location && (
                            <div className='accommodation-location'>
                                <MapPin size={16} />
                                <span>{location}</span>
                            </div>
                        )}

                        {/* Rating */}
                        <div className='accommodation-rating'>
                            <Star size={16} fill='currentColor' />

                            <strong>{ratingValue > 0 ? ratingValue.toFixed(1) : 'New'}</strong>

                            {reviewCount > 0 && <span>({reviewCount} Reviews)</span>}
                        </div>

                        {/* Tags */}
                        <div className='accommodation-tags'>
                            <span className='accommodation-tag type'>
                                <Home size={14} />
                                {formattedPropertyType}
                            </span>

                            {formattedCategory && (
                                <span className='accommodation-tag category'>{formattedCategory}</span>
                            )}

                            {units.length > 0 && (
                                <span className='accommodation-tag units'>
                                    <Layers3 size={14} />
                                    {units.length} {units.length === 1 ? 'Unit Type' : 'Unit Types'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* =====================================
                        PRICE
                    ===================================== */}

                    <div className='accommodation-price'>
                        {startingPrice !== null ? (
                            <>
                                <strong>₹{startingPrice.toLocaleString('en-IN')}</strong>

                                <span>/{priceUnitLabel}</span>
                            </>
                        ) : (
                            <span className='price-not-available'>Price not set</span>
                        )}
                    </div>
                </div>

                {/* =========================================
                    INFO ROW
                ========================================= */}

                <div className='accommodation-stats'>
                    <div className='accommodation-div'>
                        <div className='accommodation-stat'>
                            <BedDouble size={17} />

                            <div>
                                <span className='stat-value'>{units.length}</span>

                                <span className='stat-label'>Unit Types</span>
                            </div>
                        </div>

                        <div className='accommodation-stat'>
                            <Layers3 size={17} />

                            <div>
                                <span className='stat-value'>{totalInventory}</span>

                                <span className='stat-label'>Total Units</span>
                            </div>
                        </div>

                        <div className='accommodation-stat'>
                            <Users size={17} />

                            <div>
                                <span className='stat-value'>{maxGuests || 0}</span>

                                <span className='stat-label'>Max Guests</span>
                            </div>
                        </div>
                    </div>
                    <div className=''>
                        <div className='accommodation-actions'>
                            {onDuplicate && (
                                <button
                                    type='button'
                                    className='accommodation-action-btn'
                                    onClick={stopAndRun(() => onDuplicate(accommodation._id))}
                                    title='Duplicate'
                                >
                                    <Copy size={20} />
                                </button>
                            )}

                            {onEdit && (
                                <button
                                    type='button'
                                    className='accommodation-action-btn edit'
                                    onClick={stopAndRun(() => onEdit(accommodation._id))}
                                    title='Edit'
                                >
                                    <Edit3 size={20} />
                                </button>
                            )}

                            {onDelete && (
                                <button
                                    type='button'
                                    className='accommodation-action-btn delete'
                                    onClick={stopAndRun(() => onDelete(accommodation._id))}
                                    title='Delete'
                                >
                                    <Trash2 size={20} />
                                </button>
                            )}

                            {onReject && status === 'active' && (
                                <button
                                    type='button'
                                    className='accommodation-action-btn reject'
                                    onClick={stopAndRun(() => onReject(accommodation._id))}
                                    title='Reject / Disable'
                                >
                                    <ShieldX size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* =========================================
                    ACTIONS
                ========================================= */}
            </div>
        </div>
    );
};

export default AccommodationCard;
